import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { append, findIndex, isEmpty, pluck, propEq, update } from 'ramda';
import { useLocation, useNavigate } from 'react-router-dom';
import { questionsQuery } from 'pages/PatientRecords/PatientRecords';
import { PAGE_TABS } from 'components/VisitRecords/VisitRecords';
import Skeleton from 'react-loading-skeleton';
import Graphic from 'components/Graphic/Graphic';
import { formatArrayIntoSentence, isRole, moveFilteredItemsToTop } from '../../utils/utils';
import { showAlert, useAppContext, usePatient } from '../../context/appContext';
import MedicalHistoryForm from '../../components/Modal/MedicalHistoryForm/MedicalHistoryForm';
import { Answer, HTTPError, Patient, Question } from '../../types/types';
import { updatePatientMedHistory } from '../../api/api';

function MedicalHistory() {
  const [{ user, patients }, dispatch] = useAppContext();
  const { patient } = usePatient();
  const [showMedicalForm, setShowMedicalForm] = React.useState(false);
  const [medicalQuestion, setMedicalQuestion] = React.useState<Question>();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isLoading, isFetching } = useQuery({
    ...questionsQuery('MEDICAL-HISTORY'),
    enabled: !!patient,
  });

  const { mutate: updatePatientMedHistoryFn, isLoading: isUpdatingMedHistory } = useMutation(
    (variables: { patientId: string; payload: Record<string, any> }) =>
      updatePatientMedHistory(variables.patientId, variables.payload),
    {
      onSuccess: (_, variables) => {
        const updatePatients = patients.map(fndPatient => ({
          ...fndPatient,
          ...(fndPatient.id === variables.patientId && {
            patientDetails: {
              ...fndPatient.patientDetails,
              medicalHistory: variables.payload.medicalHistory,
            },
          }),
        })) satisfies Patient[];

        dispatch({ type: 'SET_ASSIGNED_PATIENTS', payload: updatePatients });

        setShowMedicalForm(false);

        setMedicalQuestion(undefined);
      },
      onError: (error: HTTPError) => {
        showAlert(dispatch, {
          show: true,
          type: 'ERROR',
          message: error.message,
        });
      },
    },
  );

  let fetchedQuestions = (data || []) as Question[];

  fetchedQuestions = moveFilteredItemsToTop({
    array: fetchedQuestions,
    findFunction: (item: Question) =>
      ['CURRENT_MEDICATION_OR_SUPPLEMENTS', 'CURRENT_ALLERGIES', 'PAST_SURGERIES'].includes(
        item.id,
      ),
  });

  const findAnswersGiven = (questionId: string) => {
    if (patient && patient.patientDetails?.medicalHistory?.length > 0) {
      const question = patient?.patientDetails?.medicalHistory.find(
        fndQuestion => fndQuestion.questionKey === questionId,
      );

      return question?.answers || [];
    }

    return [];
  };

  const formatSelectedAnswers = (questionId: string, options: any[]) => {
    const answers =
      findAnswersGiven(questionId).length === 1 &&
      findAnswersGiven(questionId)[0].includes('OTHER ->')
        ? ['OTHER']
        : findAnswersGiven(questionId);

    const answersTexts = (options || [])
      ?.filter(fndOption => answers.includes(fndOption?.id))
      ?.map(fndOption => fndOption?.text);

    return formatArrayIntoSentence(answersTexts);
  };

  const handleMedHistoryUpdate = (answer: Answer) => {
    if (!(patient && patient.patientDetails?.medicalHistory?.length > 0)) {
      return;
    }

    let medicalHistories = patient.patientDetails.medicalHistory;

    const tempAnsIndex = findIndex(propEq('questionKey', answer.questionKey), medicalHistories);

    if (tempAnsIndex === -1) {
      medicalHistories = append(answer, medicalHistories);
    }

    medicalHistories = update(tempAnsIndex, answer, medicalHistories);

    updatePatientMedHistoryFn({
      patientId: patient.id,
      payload: {
        medicalHistory: medicalHistories,
      },
    });
  };

  const handleNextTab = () => {
    const tab = 'medical-history';

    const indexOfActiveTab = PAGE_TABS.findIndex(fndTab => fndTab.id === tab);

    const nextTab = PAGE_TABS.find((_, index) => index === indexOfActiveTab + 1);

    if (nextTab) {
      const tabURL = `${location.pathname.replace(tab, nextTab.id)}${location.search}`;
      navigate(tabURL);
    }
  };

  const renderLoading = () => (
    <div className="flex flex-col">
      <div className="w-full grid tablet:grid-cols-2 gap-6 mb-7">
        {new Array(5).fill(null).map((_, index) => (
          <div key={index}>
            <h3 className="text-sm font-medium">
              <Skeleton width="70%" height={25} duration={1.4} />
            </h3>
            <div className="relative flex flex-row w-full space-x-3 mt-3">
              <Skeleton width="100%" height={20} duration={1.4} className="block !w-[7rem]" />
              <Skeleton width="100%" height={20} duration={1.4} className="block !w-[14rem]" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col pt-0 pb-10 tablet:pt-6 tablet:pb-7">
      {isFetching && isLoading && isEmpty(fetchedQuestions) ? (
        <>{renderLoading()}</>
      ) : fetchedQuestions.length === 0 ? (
        <div className="flex flex-col justify-center items-center mt-8 mb-20">
          <Graphic id="graphicNoPendingVisit" className="h-[130px]" />
          <div className="text-center mt-3">
            <p className="font-medium">You’re all caught up.</p>
            <p className="text-[#7A7A7A] text-xs">
              {`${patient.firstName}`}’s Medical history will show here
            </p>
          </div>
        </div>
      ) : (
        <div className="fs-exclude w-full grid tablet:grid-cols-2 tablet:gap-8 gap-6 mb-7">
          {fetchedQuestions.map(fndQuestion => (
            <div key={fndQuestion.id}>
              <h3 className="text-sm font-medium">
                {fndQuestion.id === 'CURRENT_MEDICATION_OR_SUPPLEMENTS'
                  ? 'Active Medication'
                  : fndQuestion.id === 'CURRENT_ALLERGIES'
                  ? 'Allergies'
                  : fndQuestion.id === 'PAST_SURGERIES'
                  ? 'Surgical History'
                  : fndQuestion?.text || fndQuestion?.option}
              </h3>
              <p className="text-[15px] font-semibold mt-0 tablet:mt-2">
                Answer:{' '}
                <span className="text-[15px] font-medium text-[#676767] underline underline-offset-2">
                  {(fndQuestion.options && fndQuestion.options?.length > 0
                    ? formatSelectedAnswers(fndQuestion.id, fndQuestion.options)
                    : findAnswersGiven(fndQuestion.id)[0]) || 'N/A'}

                  {!isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') && (
                    <button
                      disabled={!patient.patientDetails?.medicalHistory}
                      onClick={() => {
                        setMedicalQuestion(fndQuestion);
                        setShowMedicalForm(true);
                      }}
                      type="button"
                      className="text-[13px] ml-2 text-river-blue disabled:opacity-50"
                    >
                      edit
                    </button>
                  )}
                </span>
              </p>
              {fndQuestion.options && fndQuestion.options?.length > 0 && (
                <p className="text-xs font-medium mt-0 tablet:mt-2 text-[#9DA4A8]">
                  Options:{' '}
                  <span className="text-xs font-light text-[#9DA4A8]">
                    {formatArrayIntoSentence(pluck('text', fndQuestion.options))}
                  </span>
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {!(isFetching && isLoading) && (
        <button
          type="button"
          onClick={() => handleNextTab()}
          className="border border-river-blue text-river-blue rounded-full px-5 py-[9px] text-sm mt-6 w-[180px] 
              hover:bg-[#EEFBFF]"
        >
          Next
        </button>
      )}

      {medicalQuestion && showMedicalForm && (
        <MedicalHistoryForm
          open={showMedicalForm}
          question={medicalQuestion}
          isUpdatingMedHistory={isUpdatingMedHistory}
          answers={[
            {
              questionKey: medicalQuestion.id,
              answers: findAnswersGiven(medicalQuestion.id),
            },
          ]}
          onCloseModal={() => {
            setShowMedicalForm(false);
            setMedicalQuestion(undefined);
          }}
          onUpdateMedicalHistory={answers => {
            handleMedHistoryUpdate(answers[0]);
          }}
        />
      )}
    </div>
  );
}

export default MedicalHistory;
